import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { authClient } from '../lib/api';
import { useAuthError } from '../lib/auth-errors';
import { useT } from '../lib/i18n';
import { Button, ErrorNote, Field } from './ui';

type Step = { kind: 'idle' } | { kind: 'password'; then: 'enable' | 'disable' | 'codes' } | { kind: 'scan'; uri: string; codes: string[] } | { kind: 'codes'; codes: string[] };

/**
 * Whether to show a "switch it on first" screen. Stays true while the setup is in progress, so
 * fresh account data halfway (it's on now) doesn't whisk away the backup codes.
 */
export function useTwoFactorGate(needed: boolean) {
  const [active, setActive] = useState(false);
  return { show: needed || active, onActive: setActive };
}

/** A 6-digit code field that phones fill in from the authenticator app or SMS suggestions. */
export function CodeField({ id, label, backup = false }: { id: string; label: string; backup?: boolean }) {
  return backup ? (
    <Field id={id} name="code" label={label} autoComplete="off" autoCapitalize="off" spellCheck={false} required maxLength={24} />
  ) : (
    <Field id={id} name="code" label={label} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required />
  );
}

/**
 * Switch two-step verification on (password → scan → first code → backup codes) or off.
 * `required`: the person can't switch it off (owner of a paid salon, our own team); `requiredNote` says why.
 */
export function TwoFactorSetup({ enabled, required = false, requiredNote, onChange, onActive }: { enabled: boolean; required?: boolean; requiredNote?: string; onChange?: () => void; onActive?: (active: boolean) => void }) {
  const t = useT();
  const authError = useAuthError();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    onActive?.(step.kind !== 'idle');
  }, [step.kind, onActive]);

  useEffect(() => {
    if (step.kind === 'scan') void QRCode.toDataURL(step.uri, { margin: 1, width: 360, color: { dark: '#1b201e', light: '#ffffff' } }).then(setQr);
  }, [step]);

  const changed = () => {
    // Refetch everything (keeps what's on screen until the new data is in).
    void qc.invalidateQueries();
    onChange?.();
  };

  async function withPassword(e: FormEvent<HTMLFormElement>, then: 'enable' | 'disable' | 'codes') {
    e.preventDefault();
    const password = String(new FormData(e.currentTarget).get('password'));
    setBusy(true);
    setError(null);
    if (then === 'enable') {
      const { data, error: err } = await authClient.twoFactor.enable({ password, issuer: 'KoalaSpot' });
      setBusy(false);
      if (err || !data || !('totpURI' in data)) return setError(authError(err));
      setStep({ kind: 'scan', uri: data.totpURI!, codes: data.backupCodes ?? [] });
    } else if (then === 'disable') {
      const { error: err } = await authClient.twoFactor.disable({ password });
      setBusy(false);
      if (err) return setError(authError(err));
      setStep({ kind: 'idle' });
      changed();
    } else {
      const { data, error: err } = await authClient.twoFactor.generateBackupCodes({ password });
      setBusy(false);
      if (err || !data) return setError(authError(err));
      setStep({ kind: 'codes', codes: data.backupCodes });
    }
  }

  async function confirm(e: FormEvent<HTMLFormElement>, codes: string[]) {
    e.preventDefault();
    const code = String(new FormData(e.currentTarget).get('code')).replace(/\s/g, '');
    setBusy(true);
    setError(null);
    const { error: err } = await authClient.twoFactor.verifyTotp({ code });
    setBusy(false);
    if (err) return setError(authError(err));
    setStep({ kind: 'codes', codes });
  }

  if (step.kind === 'password') {
    return (
      <form onSubmit={(e) => withPassword(e, step.then)} className="flex max-w-sm flex-col gap-3">
        {step.then === 'disable' && <p className="text-sm text-muted">{t('tf.disableWhy')}</p>}
        <Field id="tf-password" name="password" type="password" autoComplete="current-password" required label={t('tf.password')} hint={step.then === 'disable' ? undefined : t('tf.passwordWhy')} autoFocus />
        <ErrorNote message={error} />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>
            {step.then === 'disable' ? t('tf.disable') : t('tf.continue')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setStep({ kind: 'idle' })}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    );
  }

  if (step.kind === 'scan') {
    const secret = new URL(step.uri).searchParams.get('secret') ?? '';
    return (
      <form onSubmit={(e) => confirm(e, step.codes)} className="flex flex-col gap-4">
        <p>{t('tf.step1')}</p>
        <div className="flex flex-col gap-2">
          <p>{t('tf.step2')}</p>
          {qr && <img src={qr} alt="QR" className="size-44 rounded-[var(--radius-control)] border border-line" />}
          <p className="text-sm text-muted">{t('tf.manual')}</p>
          <code className="self-start break-all rounded-[var(--radius-control)] bg-subtle px-3 py-2 text-sm">{secret.match(/.{1,4}/g)?.join(' ')}</code>
        </div>
        <p>{t('tf.step3')}</p>
        <div className="max-w-[220px]">
          <CodeField id="tf-code" label={t('tf.code')} />
        </div>
        <ErrorNote message={error} />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy}>
            {t('tf.confirm')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setStep({ kind: 'idle' })}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    );
  }

  if (step.kind === 'codes') {
    return (
      <BackupCodes
        codes={step.codes}
        onDone={() => {
          setStep({ kind: 'idle' });
          changed();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium">{enabled ? `✓ ${t('tf.on')}` : t('tf.off')}</p>
      {enabled && required && <p className="text-sm text-muted">{requiredNote ?? t('tf.requiredOff')}</p>}
      <div className="flex flex-wrap gap-2">
        {!enabled && <Button onClick={() => setStep({ kind: 'password', then: 'enable' })}>{t('tf.start')}</Button>}
        {enabled && (
          <Button variant="secondary" onClick={() => setStep({ kind: 'password', then: 'codes' })}>
            {t('tf.newCodes')}
          </Button>
        )}
        {enabled && !required && (
          <Button variant="ghost" onClick={() => setStep({ kind: 'password', then: 'disable' })}>
            {t('tf.disable')}
          </Button>
        )}
      </div>
      {enabled && <p className="text-sm text-muted">{t('tf.newCodesHint')}</p>}
    </div>
  );
}

function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  const file = `data:text/plain;charset=utf-8,${encodeURIComponent(`KoalaSpot herstelcodes / backup codes\n\n${text}\n`)}`;
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-semibold">{t('tf.backupTitle')}</h3>
      <p className="max-w-prose text-sm text-muted">{t('tf.backupIntro')}</p>
      <ul className="grid max-w-sm grid-cols-2 gap-x-6 gap-y-1 rounded-[var(--radius-control)] bg-subtle px-4 py-3 font-mono text-[15px]">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={async () => {
            await navigator.clipboard.writeText(text);
            setCopied(true);
          }}
        >
          {copied ? `✓ ${t('tf.copied')}` : t('tf.copy')}
        </Button>
        <a href={file} download="lumi-herstelcodes.txt" className="inline-flex min-h-11 items-center rounded-full border border-line px-4 text-[15px] font-semibold hover:bg-subtle">
          {t('tf.download')}
        </a>
        <Button onClick={onDone}>{t('tf.done')}</Button>
      </div>
    </div>
  );
}
